'use client';

import Link from 'next/link';
import type { ComponentPropsWithoutRef, MouseEvent, ReactNode } from 'react';

import type { IConversionCtaAction } from '../_data/blog-conversion-cta';
import { trackBlogCtaClick as trackBlogCtaAnalyticsClick } from '@/app/_lib/analytics-events';
import { trackBlogCtaClick as trackBlogCtaLocationClick, type TBlogCtaLocation } from '@/app/_utils/analytics';
import { cn } from '@/app/_utils/cn';

import FunnelEmailPopoverCta from './FunnelEmailPopoverCta';

/** Base shell — links also use `blog-funnel-ignore` so `.article-content a` prose doesn’t tint hover */
export const FUNNEL_CTA_SHELL =
  [
    'blog-funnel-ignore inline-flex w-full min-h-12 shrink-0 cursor-pointer items-center justify-center gap-2',
    'rounded-xl px-6 py-3',
    'text-[0.9375rem] font-semibold tracking-[-0.01em] text-center',
    'transition-[transform,box-shadow,border-color,background-color,color] duration-200 motion-reduce:transition-none',
    'ease-out no-underline',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/45 focus-visible:ring-offset-2',
  ].join(' ');

/** Primary — light surface (editorial CTA) + gradient label (brand spectrum) */
export const FUNNEL_PRIMARY =
  [
    'border border-border/70 bg-linear-to-br from-card to-[hsl(var(--accent)/0.07)]',
    'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.85),0_2px_10px_-3px_rgba(15,23,42,0.1)]',
    'hover:-translate-y-px',
    'hover:border-accent/25 hover:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.92),0_8px_22px_-5px_rgba(15,23,42,0.14)]',
    'active:translate-y-0',
    'motion-reduce:hover:translate-y-0',
  ].join(' ');

/** Text layer — violet → teal → emerald (readable on cream/white fills) */
export const FUNNEL_PRIMARY_LABEL =
  [
    'inline bg-linear-to-r',
    'from-[#6938ea] via-[#0ea5bf] to-[#059669]',
    'bg-clip-text font-semibold text-transparent tracking-[-0.02em]',
  ].join(' ');

/** Secondary — neutral surface; hover = clean border lift, no purple/wash fill */
export const FUNNEL_SECONDARY =
  [
    'border border-border/85 bg-card text-foreground',
    'shadow-[0_1px_2px_rgba(15,23,42,0.05)]',
    'hover:-translate-y-px',
    'hover:border-accent/40 hover:bg-card hover:shadow-[0_4px_14px_-4px_rgba(15,23,42,0.1)]',
    'active:translate-y-0',
    'motion-reduce:hover:translate-y-0',
  ].join(' ');

export const FUNNEL_TERTIARY =
  [
    'min-h-0 w-auto cursor-pointer px-2 py-2',
    'text-[0.9375rem] font-medium text-muted-foreground',
    'underline-offset-[5px] decoration-transparent hover:underline hover:text-foreground',
    'hover:-translate-y-px motion-reduce:hover:translate-y-0',
  ].join(' ');

const externalTabProps = (href: string) =>
  href.startsWith('http') ? ({ target: '_blank' as const, rel: 'noopener noreferrer' as const }) : {};

interface IFunnelNavAnchorProps {
  href: string;
  className: string;
  children: ReactNode;
  /** GA4 `blog_cta_click` — 기본 링크 동작은 유지 */
  onTrackedClick?: () => void;
}

/** `mailto:` — native <a>; GA 전송 후 명시적으로 동일 href로 이동(일부 환경 기본 mailto 무시 완화) */
const FunnelNavAnchor = ({ href, className, children, onTrackedClick }: IFunnelNavAnchorProps) => {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onTrackedClick?.();
    if (!href.startsWith('mailto:')) {
      return;
    }
    e.preventDefault();
    window.location.assign(href);
  };

  if (href.startsWith('mailto:')) {
    return (
      <a href={href} className={className} onClick={handleClick}>
        {children}
      </a>
    );
  }

  const handleLinkClick = () => {
    onTrackedClick?.();
  };

  return (
    <Link href={href} className={className} {...externalTabProps(href)} onClick={handleLinkClick}>
      {children}
    </Link>
  );
};

interface IFunnelCtaNavigationProps extends Omit<ComponentPropsWithoutRef<'nav'>, 'children'> {
  actions: readonly IConversionCtaAction[];
  /** Omit top margin inside nav (caller adds spacing above) */
  compact?: boolean;
  /** GA4 → `blog_cta_click.page` — 포스트 슬러그 문자열 */
  analyticsPageSlug?: string;
  /** GA4 → `blog_cta_click.section` 예: bottom_cta */
  analyticsSection?: string;
  /** Simple location tracking for global click distribution */
  location?: TBlogCtaLocation;
}

const FunnelCtaNavigation = ({
  actions,
  compact = false,
  className,
  analyticsPageSlug,
  analyticsSection = 'bottom_cta',
  location,
  ...navProps
}: IFunnelCtaNavigationProps) => {
  const primary = actions.filter((a) => a.variant === 'primary');
  const secondary = actions.filter((a) => a.variant === 'secondary');
  const tertiary = actions.filter((a) => a.variant === 'tertiary');

  const trackedClick =
    analyticsPageSlug && analyticsSection
      ? (action: IConversionCtaAction) => {
          const ctaAnalyticsName = action.ctaAnalyticsName;
          if (!ctaAnalyticsName) return undefined;
          return () => {
            trackBlogCtaAnalyticsClick({
              cta_name: ctaAnalyticsName,
              page: analyticsPageSlug,
              section: analyticsSection,
            });
            if (location) {
              trackBlogCtaLocationClick({
                location,
                page: analyticsPageSlug,
              });
            }
          };
        }
      : () => undefined;

  return (
    <nav
      {...navProps}
      className={cn(
        'blog-funnel-cta-root relative z-2 flex w-full flex-col items-center gap-3 sm:gap-4',
        !compact && 'mt-8 sm:mt-10',
        className,
      )}
    >
      {primary.length > 0 && (
        <div className='flex w-full max-w-88 flex-col gap-3 sm:w-auto'>
          {primary.map((action) => (
            <FunnelNavAnchor
              key={`${action.href}-${action.label}`}
              href={action.href}
              className={cn(FUNNEL_CTA_SHELL, FUNNEL_PRIMARY)}
              onTrackedClick={trackedClick(action)}
            >
              <span className={FUNNEL_PRIMARY_LABEL}>{action.label}</span>
            </FunnelNavAnchor>
          ))}
        </div>
      )}

      {secondary.length > 0 && (
        <ul role='list' className='mx-auto grid w-full max-w-xl grid-cols-1 gap-3 sm:grid-cols-2'>
          {secondary.map((action) => (
            <li key={`${action.href}-${action.label}`} className='flex w-full justify-center'>
              {action.ctaEmailPopover?.email ? (
                <FunnelEmailPopoverCta
                  label={action.label}
                  email={action.ctaEmailPopover.email}
                  className={cn(FUNNEL_CTA_SHELL, FUNNEL_SECONDARY, 'sm:min-w-54')}
                  onTrackedClick={trackedClick(action)}
                />
              ) : (
                <FunnelNavAnchor
                  href={action.href}
                  className={cn(FUNNEL_CTA_SHELL, FUNNEL_SECONDARY, 'sm:min-w-54')}
                  onTrackedClick={trackedClick(action)}
                >
                  {action.label}
                </FunnelNavAnchor>
              )}
            </li>
          ))}
        </ul>
      )}

      {tertiary.length > 0 && (
        <ul role='list' className='flex flex-col flex-wrap items-center justify-center gap-x-8 gap-y-2 sm:flex-row'>
          {tertiary.map((action) => (
            <li key={`${action.href}-${action.label}`}>
              <FunnelNavAnchor
                href={action.href}
                className={cn('blog-funnel-ignore', FUNNEL_TERTIARY)}
                onTrackedClick={trackedClick(action)}
              >
                {action.label}
              </FunnelNavAnchor>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
};

export default FunnelCtaNavigation;
